import React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useProfileUpdate } from '../../hooks/auth/useProfileUpdate';
import { Building, MapPin, Briefcase, Linkedin, Phone } from 'lucide-react';
import { SkillAssessmentForm } from '../career/SkillAssessmentForm';
import type { Database } from '../../types/database';

type Profile = Database['public']['Tables']['user_profiles']['Row'];

const CITIES = [
  'New York City, NY',
  'Washington, D.C.',
  'San Francisco, CA',
  'Boston, MA',
  'Chicago, IL',
  'Seattle, WA',
  'Miami, FL',
  'Los Angeles, CA',
  'Austin, TX',
  'Houston, TX',
  'Dallas, TX',
  'Atlanta, GA',
  'Toronto, ON',
  'Vancouver, BC',
  'London, UK',
  'Amsterdam, Netherlands',
  'Sydney, Australia',
  'Singapore',
  'USA',
  'Africa',
  'Other Canada',
  'Other Outside of North America',
  'Other'
];

const EXPERIENCE_LEVELS = [
  'College',
  'Early Career',
  'Mid-Level',
  'Senior Level',
  'Executive/Chief'
];

const EXPERTISE_LEVELS = [
  'None',
  'Founder',
  'Graduate/Trainee',
  'Advanced Individual Contributor',
  'Senior Individual Contributor',
  'Team Lead',
  'Junior Manager',
  'Senior Manager',
  'Director',
  'SVP (Senior Vice President)',
  'VP (Vice President)',
  'C-Level Executive',
  'CEO/President'
];

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Manufacturing',
  'Retail',
  'Media',
  'Consulting',
  'Real Estate',
  'Energy',
  'Transportation',
  'Hospitality',
  'Government',
  'Non-profit'
];

interface ProfileFormProps {
  profile: Profile | null;
}

export function ProfileForm({ profile }: ProfileFormProps) {
  const { isUpdating, updateProfile } = useProfileUpdate();
  const [showSkillForm, setShowSkillForm] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      linkedin_url: profile?.linkedin_url || '',
      phone_number: profile?.phone_number || '',
      gender: profile?.gender || '',
      date_of_birth: profile?.date_of_birth ? new Date(profile.date_of_birth).toISOString().split('T')[0] : '',
      company: profile?.company || '',
      position: profile?.position || '',
      country: profile?.country || '',
      location: profile?.location || '',
      experience_level: profile?.experience_level || '',
      expertise_level: profile?.expertise_level || '',
      industries: profile?.industries || []
    }
  });

  const onSubmit = async (data: Partial<Profile>) => {
    // Clean up the data before submission
    const cleanedData = {
      ...data,
      // Only include date if it's not empty
      date_of_birth: data.date_of_birth ? data.date_of_birth : null,
      // Ensure industries is an array
      industries: Array.isArray(data.industries) ? data.industries : []
    };

    await updateProfile(cleanedData);
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black">
              First Name
            </label>
            <input
              type="text"
              {...register('first_name', { required: 'First name is required' })}
              className="mt-1 input w-full"
            />
            {errors.first_name && (
              <p className="mt-1 text-sm text-red-500">{errors.first_name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-black">
              Last Name
            </label>
            <input
              type="text"
              {...register('last_name', { required: 'Last name is required' })}
              className="mt-1 input w-full"
            />
            {errors.last_name && (
              <p className="mt-1 text-sm text-red-500">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black">
              LinkedIn URL
            </label>
            <div className="relative">
              <Linkedin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="url"
                {...register('linkedin_url')}
                className="mt-1 input w-full pl-10"
                placeholder="http://www.linkedin.com/X"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-black">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                {...register('phone_number')}
                className="mt-1 input w-full pl-10"
              />
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black">
              Gender
            </label>
            <select
              {...register('gender')}
              className="mt-1 input w-full"
            >
              <option value="">Select gender</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-black">
              Date of Birth
            </label>
            <input
              type="date"
              {...register('date_of_birth')}
              max={new Date().toISOString().split('T')[0]}
              className="mt-1 input w-full"
            />
          </div>
        </div>

        {/* Professional Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black">
              Current Company/School
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                {...register('company')}
                className="mt-1 input w-full pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-black">
              Current Title/Position
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                {...register('position')}
                className="mt-1 input w-full pl-10"
              />
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black">
              Country
            </label>
            <select
              {...register('country')}
              className="mt-1 input w-full"
            >
              <option value="">Select country</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-black">
              Closest Major City
            </label>
            <select
              {...register('location')}
              className="mt-1 input w-full"
            >
              <option value="">Select city</option>
              {CITIES.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Experience and Expertise */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black">
              Professional Experience
            </label>
            <select
              {...register('experience_level')}
              className="mt-1 input w-full"
            >
              <option value="">Select experience level</option>
              {EXPERIENCE_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-black">
              Professional Expertise
            </label>
            <select
              {...register('expertise_level')}
              className="mt-1 input w-full"
            >
              <option value="">Select expertise level</option>
              {EXPERTISE_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Industries */}
        <div>
          <label className="block text-sm font-medium text-black">
            Industries
          </label>
          <select
            multiple
            {...register('industries')}
            className="mt-1 input w-full h-32"
          >
            {INDUSTRIES.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Hold Ctrl/Cmd to select multiple industries
          </p>
        </div>
      </div>

      {/* Skills Management */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-black">Skills & Expertise</h3>
          <button
            type="button"
            onClick={() => setShowSkillForm(true)}
            className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors"
          >
            Manage Skills
          </button>
        </div>
        <p className="text-sm text-gray-500">
          Update your technical and professional skills to improve job matches and career recommendations.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isUpdating}
          className="btn-primary"
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      </form>

      {showSkillForm && (
        <SkillAssessmentForm
          onClose={() => setShowSkillForm(false)}
          onSuccess={() => {
            setShowSkillForm(false);
            // Refresh profile data after skills update
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}